import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../models/user';
import { AppService } from '../../services/app.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { GlobalService } from '../../services/global.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';
import { UserService } from '../../services/user.service';
import { ModalComponent } from '../modal/modal.component';
import { PasswordResetModalComponent } from '../password-reset-modal/password-reset-modal.component';
import { CustomizationService } from 'src/app/services/customization.service';
import { SsoService } from 'src/app/services/sso.service';

@Component({
	selector: 'app-login-modal',
	templateUrl: './login-modal.html',
	styleUrls: ['./login-modal.scss']
})

export class LoginModalComponent implements OnInit, OnDestroy {
	public buildTimestamp: any;
	public companyLogo: string;
	public form: UntypedFormGroup;
	public selectedCustomer: any;
	public selectedTab = 'inet';
	public showPassword = false;
	public subscriptionsUnsubscribe = new Subject<void>();
	public user: User;
	@ViewChild('username') public username: ElementRef;
	@ViewChild('password') public password: ElementRef;
	public version: any;
	public year: number;
	public widgetIsShown: boolean = false;
	public displayPasswordBox: boolean = false;
	public hasSso: boolean = false;

	constructor(private _appService: AppService,
				private _router: Router,
				public dialogRef: MatDialogRef<LoginModalComponent>,
				@Inject(MAT_DIALOG_DATA) public data: any,
				private _dialog: MatDialog,
				private _errorService: ErrorService,
				private _formBuilder: UntypedFormBuilder,
				private _snackbarService: SnackbarService,
				private _spinnerService: SpinnerService,
				private _authenticationService: AuthenticationService,
				private _userService: UserService,
				private _globalService: GlobalService,
				private _cdRef: ChangeDetectorRef,
				private _customerService: CustomerService,
				private _customizationService: CustomizationService,
				private _ssoService: SsoService) {
	}

	close() {
		this.dialogRef.close();
	}

	continue() {
		//if we can see the password box, we shouldn't be able to see the continue button anyway,
		//so there's nothing to do
		if(this.displayPasswordBox)
			return;

		let username: string = this.form.get('username')?.value;

		//in this case validation will appear to let the user know to enter a username,
		//but we still have to prevent the rest of this method from executing
		if(!username || username.length == 0)
			return;

    username = username.toLocaleLowerCase();

		let parts = username.split('@');
		if (parts.length == 2) { //valid email address
			let domains = this.user?.globalSettings?.ssoDomains;
			let matches = domains.filter((d) => d === parts[1]);
			if (matches.length == 1) { //because domains are unique
				let info = {
					username: username,
					domain: matches[0]
				};
				localStorage.setItem('RapidShip:ssoInfo', JSON.stringify(info));

				this._ssoService.validateSsoLogin(username).subscribe({
					next: () => {
						window.location.href = this._ssoService.ssoGetLoginUrl(username);				
					}, error: (error) => {
						this._errorService.error(error, 'login Failed', 'modal');
					}
				});
				return;
			}
		}
		//if we get this far, we're not using SSO
		this.displayPasswordBox = true;
		setTimeout(() => { //the change in displayPasswordBox doesn't propagate to the ngIfs instantly, so we have to wait a bit for this.password to be defined
			this.password.nativeElement.focus();
		}, 100);
	}

	forgotPassword() {
		const dialogRef = this._dialog.open(PasswordResetModalComponent);
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				if (result === true) {
					this._snackbarService.openSnackBar(
						'We will email you a password reset link if we have the email address on record.',
						'Close', 5000, 'success');
				}
			}
		});
	}

	guestLogin() {
		this.selectTab('inet');
		this._cdRef.detectChanges();
		this.form.get('username').setValue('guestuser9999');
		this.form.get('password').setValue('guestuser9999');
		this.login(this.form);
	}

	login(form) {
		if (form.valid) {
			if (form.value.username.toString().length > 0 && form.value.password.toString().length > 0) {
				this._spinnerService.startSpinner();
				this.showPassword = false;
				this._authenticationService.initiateLogin(form.value, this.selectedTab).subscribe({
					next: () => {
						this._spinnerService.stopSpinner();
						this.onLogin();
					}, error: (error) => {
						this._spinnerService.stopSpinner();
						this._errorService.error(error, 'login Failed', 'modal');
						this._userService.deleteUser();
					}
				});
			}
		} else {
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}

	ngOnDestroy() {
		this.subscriptionsUnsubscribe.next();
	}

	ngOnInit() {
		this._appService.companyLogo$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((companyLogo) => {
			this.companyLogo = companyLogo;
		});
		this._userService.user$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((user) => {
			this.user = user;
			this.hasSso = this.user?.globalSettings?.isCentralizedSsoConfigured && this.user?.globalSettings?.ssoDomains?.length > 0;
			this.displayPasswordBox = !this.hasSso;
		});
		this._customizationService.widgetIsShown$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((widgetIsShown) => {
			this.widgetIsShown = widgetIsShown;
		});

		this.hasSso = this.user?.globalSettings?.isCentralizedSsoConfigured && this.user?.globalSettings?.ssoDomains?.length > 0;
		this.displayPasswordBox = !this.hasSso;

		const rememberMe: any = JSON.parse(localStorage.getItem('Rapidship:rememberMe'));
		this.selectedTab = rememberMe ? rememberMe?.userType : 'inet';
		this.form = this._formBuilder.group({
			username: [rememberMe ? rememberMe?.username : '', [Validators.required]],
			password: ['', [Validators.required]],
			rememberMe: !!rememberMe
		})
		this.buildTimestamp = this._appService.buildTimestamp;
		this.version = this._appService.version;
		this.year = this._appService.year;
		if (this.data?.error) {
			if (this.data?.error?.error?.detailedMsg === 'You are not authorized to access this application.') {
				this.data.error.error.detailedMsg = 'You have been logged out. Please enter your login information again to continue using Rapidship.';
			}
			if (this.data?.error.error.detailedMsg.indexOf('You have reached the maximum login attempts') > -1) {
				this.data.error.error.generalMsg = 'Too many failed login attempts';
			} 
			this._errorService.error(this.data?.error, 'Login Failed', 'modal');
		}
		setTimeout(() => {
			this.username?.nativeElement?.focus();
		}, 500);
	}

	onLogin() {
		if (this.selectedTab === 'inet') {
			this.selectedCustomer = this._customerService.getSelectedCustomer();
			if (this.selectedCustomer.billingStatus !== 'CREDIT_HOLD' && (this._router.url === '/' ||
				this._router.url.includes('home') ||
				this._router.url.includes('work') ||
				this._router.url.includes('login') ||
				this._router.url.includes('register') ||
				this._router.url.includes('settlements') ||
				this._router.url.includes('settlement-detail') ||
				this._router.url.includes('estimated-settlements'))) {
				this._router.navigate(['place-order']);
			} else if (this.selectedCustomer.billingStatus === 'CREDIT_HOLD' && this.user?.globalSettings?.displayInvoices && this.user?.inetUser?.viewInvoices) {
				console.warn(this.selectedCustomer.billingStatus)
				this._router.navigate(['invoices']);
				const qbiMessage = `Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention.  All invoices that have reached your ${this.selectedCustomer?.startCreditHoldEnforcementDay} aging limit must be paid in order to proceed with order placement.  Please navigate to the Invoices page or contact our customer service department for more information.`;
				const nonQbiMessage = 'Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention. Please navigate to the Invoices page or contact our customer service department for more information.';
				const dialogConfig: MatDialogConfig = {
					disableClose: true,
					data: {
						title: 'Warning',
						message: this.user?.globalSettings?.isQuickbooksEnabled ? qbiMessage : nonQbiMessage,
						okText: 'Ok'
					}
				};
				this._dialog.open(ModalComponent, dialogConfig);
			} else {
				this._router.navigate(['home']);
			}
		} else if (this.selectedTab === 'worker') {
			if (this._router.url === '/' ||
				this._router.url.includes('home') ||
				this._router.url.includes('address-book') ||
				this._router.url.includes('login') ||
				this._router.url.includes('register') ||
				this._router.url.includes('place-order') ||
				this._router.url.includes('order-confirmation') ||
				this._router.url.includes('track-order') ||
				this._router.url.includes('route-tracking') ||
				this._router.url.includes('reports') ||
				this._router.url.includes('order-templates') ||
				this._router.url.includes('invoices')) {
				this._router.navigate(['work']);
			}
		}

		// Display snackbar notification after successful login
		this._snackbarService.openSnackBar('Only the Orders that correspond to the selected customer are shown', 'OK', 5000, 'info');

		setTimeout(() => {
			this.close();
		}, 500);
	}

	removeExtraSpaces() {
		const control = this.form.get('username');
		if (control) {
			const value = control.value || '';
			control.setValue(value.trim(), { emitEvent: false });
		}
	}

	resetForm() {
		this.displayPasswordBox = false;
		this.form.get('username').setValue('');
	}

	selectTab(tab) {
		this.selectedTab = tab;
		this.form.reset();
		this.username.nativeElement.focus();
		this.displayPasswordBox = this.selectedTab === 'inet' ? !this.hasSso : true;
	}

	toggleShowPassword() {
		this.showPassword = !this.showPassword;
	}

	usernameEnter(event: KeyboardEvent) {
		if(!this.displayPasswordBox) { //we don't want to prevent form submission if we can see the password box
			event.preventDefault();
			this.continue();
		}
	}
}
